// Same as CuffsPage or ButtonsPage but adjusted for Placket
// File: PlacketsPage.tsx

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Popconfirm,
  Space,
  Pagination,
  Switch,
} from 'antd';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { createStyles } from 'antd-style';
import { theme } from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import type { TableProps, PopconfirmProps, TableColumnsType } from 'antd';
import type { Plackets } from '../../../../types/Plackets';
import PlacketModal from './PlacketModal';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table-container {
      .ant-table-body,
      .ant-table-content {
        scrollbar-width: thin;
        scrollbar-color: #eaeaea transparent;
      }
    }
  `,
}));

const PlacketsPage: React.FC = () => {
  const [plackets, setPlackets] = useState<Plackets[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Plackets | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const { styles } = useStyle();
  const currentModule = useCurrentModule();

  const cancel: PopconfirmProps['onCancel'] = (e) => {
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchPlackets = async () => {
    try {
      const data = await apiRequest<Plackets[]>({
        method: 'get',
        url: 'plackets',
      });
      setPlackets(data);
    } catch {
      showError('Failed to fetch plackets');
    }
  };

  useEffect(() => {
    fetchPlackets();
  }, []);

  const handleEditClick = (record: Plackets) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => fetchPlackets();
  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };
  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (placket_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-placket/${placket_id}`,
      });
      showSuccess('Placket deleted');
      fetchPlackets();
    } catch {
      showError('Failed to delete placket');
    }
  };

  const handleStatusToggle = async (placket_id: number) => {
    setPlackets((prev) =>
      prev.map((item) =>
        item.placket_id === placket_id
          ? { ...item, placket_status: item.placket_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-placket/${placket_id}`,
      });
      showSuccess('Placket status updated');
    } catch {
      showError('Failed to update status');
    }
  };

  const columns: TableColumnsType<Plackets> = [
    {
      title: 'Placket',
      dataIndex: 'placket_name',
      key: 'placket_name',
      width: 500,
      sorter: (a, b) => a.placket_name.localeCompare(b.placket_name),
      render: (_, record) => <div>{record.placket_name}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'placket_image',
      key: 'placket_image',
      width: 500,
      render: (_, record) =>
        record.placket_image ? (
          <img
            src={record.placket_image}
            alt="placket"
            style={{ width: 40, height: 50, objectFit: 'cover', borderRadius: 8 }}
          />
        ) : (
          '-'
        ),
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 250,
      render: (_, record) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checked={!!record.placket_status}
                onChange={() => handleStatusToggle(record.placket_id)}
              />
              <Button onClick={() => handleEditClick(record)}>
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this placket?"
              onConfirm={() => handleDelete(record.placket_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          marginBottom: 16,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Plackets</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Button type="primary" onClick={showCreateModal}>
            Add Placket
          </Button>
        )}
      </Space>

      <div style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
        <Table<Plackets>
          tableLayout="auto"
          className={styles.customTable}
          columns={columns}
          dataSource={plackets.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          rowKey="placket_id"
          pagination={false}
          scroll={{ y: 37 * 13 }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–{' '}
                {Math.min(pagination.current * pagination.pageSize, plackets.length)} of{' '}
                {plackets.length}
              </span>
              <Pagination
                {...pagination}
                total={plackets.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <PlacketModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        plackets={plackets}
      />
    </>
  );
};

export default PlacketsPage;
